import React, { forwardRef, useRef, useEffect, ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { sizes } from '@/common/styles/sizes';
import { CheckIcon } from '@/common/assets/icon/icon';

export interface CheckboxProps {
  /**
   * 체크박스 값
   */
  checked?: boolean;
  
  
  /**
   * 값이 변경될 때 호출되는 함수
   */
  onChange?: (checked: boolean) => void;
  
  /**
   * 체크박스 레이블
   */
  label?: React.ReactNode;
  
  /**
   * 비활성화 여부
   */
  disabled?: boolean;
  
  /**
   * 에러 상태 여부
   */
  error?: boolean;
  
  /**
   * 필수 항목 여부
   */
  required?: boolean;
  
  /**
   * 부분 체크 상태 (마스터 체크박스 등에서 사용)
   */
  indeterminate?: boolean;
  
  /**
   * 체크박스의 이름
   */
  name?: string;
  
  /**
   * 체크박스의 값
   */
  value?: string;
  
  /**
   * 추가 CSS 클래스
   */
  className?: string;
  
  /**
   * 입력 필드에 적용할 ID
   */
  id?: string;

  /**
   * 체크박스 사이즈
   */
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Checkbox 컴포넌트
 * 
 * 사용자가 다중 선택을 할 수 있는 체크박스 컴포넌트입니다.
 * 각종 상태(체크, 비활성화, 에러, 부분 체크 등)를 지원합니다.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      onChange,
      label,
      disabled = false,
      error = false,
      required = false,
      indeterminate = false,
      name,
      value,
      className,
      id,
      size = 'md',
    },
    ref
  ) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const resolvedRef = (ref as React.RefObject<HTMLInputElement>) || innerRef;
    
    useEffect(() => {
      if (resolvedRef && 'current' in resolvedRef && resolvedRef.current) {
        resolvedRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate, resolvedRef]);
    
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      if (disabled) return;
      onChange?.(e.target.checked);
    };

    return (
      <CheckboxContainer className={className} disabled={disabled}>
        <CheckboxWrapper>
          <HiddenCheckbox
            type="checkbox"
            checked={checked}
            onClick={e => e.stopPropagation()}
            onChange={handleChange}
            disabled={disabled}
            ref={resolvedRef}
            name={name}
            value={value}
            id={id}
          />
          <StyledCheckbox 
            checked={!!checked} 
            disabled={disabled} 
            error={error} 
            indeterminate={indeterminate}
            onClick={e => {e.stopPropagation()}}
            size={size}
          >
            {indeterminate ? (
              <Dash size={size} />
            ) : checked ? (
              <CheckIcon size={size === 'sm' ? 10 : size === 'md' ? 14 : 18} color={semanticColors.text.white} />
            ) : null}
          </StyledCheckbox>
        </CheckboxWrapper>
        
        {label && (
          <LabelText disabled={disabled}>
            {label}
            {required && <RequiredMark>*</RequiredMark>}
          </LabelText>
        )}
      </CheckboxContainer>
    );
  }
);

Checkbox.displayName = 'Checkbox';

// 스타일드 컴포넌트
const CheckboxContainer = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${sizes.gap[8]};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
`;

const CheckboxWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

interface StyledCheckboxProps {
  checked: boolean;
  disabled?: boolean;
  error?: boolean;
  indeterminate?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const StyledCheckbox = styled.div<StyledCheckboxProps>`
  width: ${props => props.size === 'sm' ? '12px' : props.size === 'md' ? '18px' : '24px'};
  height: ${props => props.size === 'sm' ? '12px' : props.size === 'md' ? '18px' : '24px'};
  border: 1.5px solid 
    ${props => {
      if (props.error) return semanticColors.border.dangerPrimary;
      if (props.checked || props.indeterminate) return semanticColors.border.brand;
      if (props.disabled) return semanticColors.border.disabled;
      return semanticColors.border.primary;
    }};
  border-radius: ${sizes.radius.sm};
  background-color: ${props => {
    if (props.checked || props.indeterminate) {
      if (props.disabled) return semanticColors.background.disabled;
      return semanticColors.background.brand;
    }
    return semanticColors.background.secondary;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  ${props => !props.disabled && !props.error && css`
    &:hover {
      border-color: ${semanticColors.border.brand};
    }
  `}
  
  ${props => props.disabled && css`
    opacity: 0.6;
  `}
`;

const Dash = styled.span<{ size?: 'sm' | 'md' | 'lg' }>`
  width: ${props => props.size === 'sm' ? '6px' : props.size === 'md' ? '8px' : '12px'};
  height: 2px;
  background-color: ${semanticColors.background.secondary};
`;

const LabelText = styled.span<{ disabled?: boolean }>`
  ${typography.paragraph.p2.default};
  color: ${props => 
    props.disabled ? semanticColors.text.disabled : semanticColors.text.secondary
  };
`;

const RequiredMark = styled.span`
  color: ${semanticColors.border.dangerPrimary};
  margin-left: ${sizes.margin[4]};
`;

export default Checkbox; 